import type { LucideIcon } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export type KpiTone = 'brand' | 'accent' | 'ink' | 'emerald' | 'rose';
export type KpiHintTone = 'up' | 'down' | 'neutral';

const TONES: Record<KpiTone, { icon: string; ring: string }> = {
  brand: { icon: 'bg-brand-50 text-brand-600', ring: 'ring-brand-100' },
  accent: { icon: 'bg-accent-50 text-accent-600', ring: 'ring-accent-100' },
  ink: { icon: 'bg-ink-100 text-ink-600', ring: 'ring-ink-200' },
  emerald: { icon: 'bg-emerald-50 text-emerald-600', ring: 'ring-emerald-100' },
  rose: { icon: 'bg-rose-50 text-rose-600', ring: 'ring-rose-100' },
};

const HINT_TONES: Record<KpiHintTone, string> = {
  up: 'text-emerald-600',
  down: 'text-rose-600',
  neutral: 'text-ink-400',
};

interface KpiCardProps {
  label: string;
  value: string;
  hint?: string;
  /** Color del `hint` según la dirección de la variación. */
  hintTone?: KpiHintTone;
  icon: LucideIcon;
  tone?: KpiTone;
  loading?: boolean;
}

/** Tarjeta de indicador clave del dashboard. */
export function KpiCard({
  label,
  value,
  hint,
  hintTone = 'neutral',
  icon: Icon,
  tone = 'brand',
  loading = false,
}: KpiCardProps) {
  const palette = TONES[tone];

  return (
    <div className="rounded-card border border-ink-100 bg-white p-5 shadow-soft transition-shadow hover:shadow-card">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-ink-500">{label}</p>

        <span
          className={cn('flex size-9 shrink-0 items-center justify-center rounded-xl ring-1', palette.icon, palette.ring)}
        >
          <Icon className="size-4.5" aria-hidden="true" />
        </span>
      </div>

      <div className="mt-3 min-w-0">
        {loading ? (
          <Skeleton className="h-7 w-28" />
        ) : (
          <p
            title={value}
            className="truncate font-mono text-lg font-bold tracking-tight text-ink-900 tabular-nums sm:text-xl"
          >
            {value}
          </p>
        )}
      </div>

      {hint ? <p className={cn('mt-1 text-xs', HINT_TONES[hintTone])}>{hint}</p> : null}
    </div>
  );
}
